import { CATEGORY_EXPORT_LABELS } from './categories';
import type { Cents, Receipt, ReceiptSession } from './types';

/**
 * Flat denormalized CSV — one row per line item, with receipt-level fields
 * (vendor / date / totals) duplicated on each row. Spreadsheet-friendly:
 * users can pivot, sum, filter without unflattening anything.
 *
 * If a receipt has no items, it still gets a single summary row with empty
 * item columns so it's not silently dropped.
 *
 * Only sessions in 'success' state with a non-null receipt are included.
 */

const HEADERS_FULL = [
  'File',
  'Vendor',
  'Date',
  'Currency',
  'Subtotal',
  'Tax',
  'Tip',
  'Total',
  'Payment',
  'Category',
  'Tags',
  'Notes',
  'Item',
  'Qty',
  'Unit Price',
  'Item Total',
] as const;

// Subtotal AND Tax both go when excluding tax — the user is hiding
// the tax amount, and total − subtotal would expose it indirectly.
const HEADERS_NO_TAX = [
  'File',
  'Vendor',
  'Date',
  'Currency',
  'Tip',
  'Total',
  'Payment',
  'Category',
  'Tags',
  'Notes',
  'Item',
  'Qty',
  'Unit Price',
  'Item Total',
] as const;

export interface CsvExportOptions {
  excludeTax?: boolean;
}

function quote(s: string): string {
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function centsToDecimal(cents: Cents): string {
  return (cents / 100).toFixed(2);
}

function dateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function receiptToRows(filename: string, r: Receipt, excludeTax: boolean): string[][] {
  const base = excludeTax
    ? [
        filename,
        r.vendor.value,
        dateString(r.date.value),
        r.currency.value,
        r.tip !== undefined ? centsToDecimal(r.tip.value) : '',
        centsToDecimal(r.total.value),
        r.paymentMethod !== undefined ? r.paymentMethod.value : '',
        r.category !== undefined ? CATEGORY_EXPORT_LABELS[r.category] : '',
        r.tags !== undefined ? r.tags.join(', ') : '',
        r.notes ?? '',
      ]
    : [
        filename,
        r.vendor.value,
        dateString(r.date.value),
        r.currency.value,
        r.subtotal !== undefined ? centsToDecimal(r.subtotal.value) : '',
        r.tax !== undefined ? centsToDecimal(r.tax.value) : '',
        r.tip !== undefined ? centsToDecimal(r.tip.value) : '',
        centsToDecimal(r.total.value),
        r.paymentMethod !== undefined ? r.paymentMethod.value : '',
        r.category !== undefined ? CATEGORY_EXPORT_LABELS[r.category] : '',
        r.tags !== undefined ? r.tags.join(', ') : '',
        r.notes ?? '',
      ];

  if (r.items.length === 0) {
    return [[...base, '', '', '', '']];
  }

  return r.items.map((item) => [
    ...base,
    item.name.value,
    String(item.qty.value),
    item.unitPrice !== undefined ? centsToDecimal(item.unitPrice.value) : '',
    centsToDecimal(item.total.value),
  ]);
}

export function exportCsvBatch(
  sessions: readonly ReceiptSession[],
  opts: CsvExportOptions = {},
): Blob {
  const excludeTax = opts.excludeTax ?? false;
  const headers = excludeTax ? HEADERS_NO_TAX : HEADERS_FULL;
  const rows: string[][] = [Array.from(headers)];
  for (const session of sessions) {
    if (session.state.kind !== 'success' || session.receipt === null) continue;
    rows.push(...receiptToRows(session.filename, session.receipt, excludeTax));
  }
  // RFC 4180 line endings.
  const csv = rows.map((r) => r.map(quote).join(',')).join('\r\n');
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}
